import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, Calendar } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { getCurrentUser, getProfile, createBooking } from "@/lib/api";

interface ScheduleModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ScheduleModal = ({ open, onOpenChange }: ScheduleModalProps) => {
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [userEmail, setUserEmail] = useState<string>("");
  const [userName, setUserName] = useState<string>("");
  const [bookingInProgress, setBookingInProgress] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  
  const calComLink = "mindcotine/30min";

  useEffect(() => {
    if (open) {
      // Reset states when opening
      setLoading(true);
      setBookingInProgress(false);
      
      // Pre-fill user email and name if logged in
      const getUserInfo = async () => {
        const userResult = await getCurrentUser();
        if (userResult.success) {
          const user = userResult.data;
          if (user.email) {
            setUserEmail(user.email);
          }
          
          // Fetch user's full name from profile
          const profileResult = await getProfile(user.id);
          if (profileResult.success && profileResult.data?.full_name) {
            setUserName(profileResult.data.full_name);
          }
        }
      };
      getUserInfo();
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;

    // Listen to Cal.com embed events
    const handleCalMessage = async (e: MessageEvent) => {
      const isCalOrigin = e.origin === "https://cal.com" ||
        e.origin === "https://app.cal.com" ||
        e.origin === "https://embed.cal.com" ||
        /\.cal\.com$/.test(new URL(e.origin).host) ||
        new URL(e.origin).host === "cal.com";

      if (!isCalOrigin) return;

      const data: Record<string, unknown> = (e.data as Record<string, unknown>) || {};
      const type = (data.event || data.type || (data?.detail as Record<string, unknown>)?.type || (data?.detail as Record<string, unknown>)?.event || "") as string;

      // Track when user starts booking
      if (type === "dateSelected" || type === "timeSelected") {
        setBookingInProgress(true);
      }

      // Booking success variations
      const successEvents = [
        "bookingSuccessful",
        "booking_successful",
        "cal:booking_successful",
        "eventScheduled",
        "__booking.confirmed"
      ];

      if (successEvents.includes(type) || /booking/i.test(type)) {
        console.log("[Cal.com] Booking successful detected:", type, data);
        
        // Save booking to database
        const userResult = await getCurrentUser();
        if (userResult.success) {
          const bookingData = {
            user_id: userResult.data.id,
            meeting_date: (data.date as string) || (data.startTime as string) || new Date().toISOString(),
            attendee_email: (data.attendeeEmail as string) || userEmail,
            attendee_name: (data.attendeeName as string) || userName,
            booking_uid: (data.uid as string) || (data.bookingUid as string) || null,
            meeting_url: (data.meetingUrl as string) || (data.url as string) || null,
            status: 'scheduled'
          };

          await createBooking(bookingData);
        }
        
        // Show success toast and close modal
        toast({
          title: t('common:schedule.success.title', 'Booking Confirmed!'),
          description: t('common:schedule.success.description', 'Your coaching session has been scheduled. Check your email for details.'),
        });
        
        onOpenChange(false);
      }

      if (type === "linkReady" || type === "ready" || type === "cal:loaded") {
        setLoading(false);
      }
    };

    window.addEventListener("message", handleCalMessage);
    
    // Fallback to hide loader after timeout
    const timeout = setTimeout(() => setLoading(false), 3000);

    return () => {
      window.removeEventListener("message", handleCalMessage);
      clearTimeout(timeout);
    };
  }, [open, onOpenChange, t, toast, userEmail, userName]);

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen && bookingInProgress) {
      // Show confirmation dialog if booking is in progress
      setShowCloseConfirm(true);
    } else {
      onOpenChange(newOpen);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="max-w-4xl h-[90vh] md:h-auto p-0 gap-0">
          <DialogHeader className="p-6 pb-4">
            <DialogTitle className="flex items-center gap-2 text-2xl">
              <Calendar className="w-6 h-6 text-primary" />
              {t('common:schedule.title', 'Schedule a Call with Your Coach')}
            </DialogTitle>
          </DialogHeader>
          
          <div className="relative overflow-hidden" style={{ height: 'calc(90vh - 100px)', minHeight: '500px', maxHeight: '750px' }}>
            {loading && (
              <div className="absolute inset-0 flex items-center justify-center bg-card z-10">
                <div className="flex flex-col items-center gap-2">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  <p className="text-sm text-muted-foreground">
                    {t('common:schedule.loadingCalendar', 'Loading calendar...')}
                  </p>
                </div>
              </div>
            )}
            <iframe
              key={language}
              src={`https://cal.com/${calComLink}?embed=true&branding=false&language=${language}${userEmail ? `&email=${encodeURIComponent(userEmail)}` : ''}${userName ? `&name=${encodeURIComponent(userName)}` : ''}`}
              width="100%"
              height="100%"
              frameBorder="0"
              className="w-full h-full"
              style={{ marginTop: '-2px' }}
              title={t('common:schedule.title', 'Schedule Appointment')}
              allow="camera; microphone; fullscreen; display-capture"
              onLoad={() => setLoading(false)}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirmation dialog for closing while booking in progress */}
      <AlertDialog open={showCloseConfirm} onOpenChange={setShowCloseConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('common:schedule.closeConfirm.title', 'Close booking?')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('common:schedule.closeConfirm.description', 'Your booking is in progress. Are you sure you want to close?')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowCloseConfirm(false)}>
              {t('common:schedule.closeConfirm.cancel', 'Continue Booking')}
            </AlertDialogCancel>
            <AlertDialogAction onClick={() => {
              setShowCloseConfirm(false);
              onOpenChange(false);
            }}>
              {t('common:schedule.closeConfirm.confirm', 'Close')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default ScheduleModal;